// Trazabilidad del caso · HU-37 (MG-ILE-69), spec `05_analisis_tecnico/Farmacovigilancia/ui/MG-ILE-69.md`.
//
// La línea de tiempo del caso en una tabla de carriles: una fila por hito, en orden cronológico
// (leída por filas es la secuencia, HU-37-C1), y una columna por rama (leída por columnas, las
// ramas corren en paralelo, C2). Lee `GET /tableros/trazabilidad/{caso_id}` (AT S3 §2.2.8).
//
// EL SERVIDOR ARMA LA LISTA: los hitos llegan ordenados, cada uno con su rama, su responsable y
// el TEXTO con tildes que se pinta (`hito.texto`); el código (`hito.evento`) no se muestra. La
// pantalla no calcula ni agrega hitos. Solo lectura: la trazabilidad no se modifica (RN-47).
//
// EXPORTAR (C9, P-04 opción b): el servidor sirve una vista imprimible con los MISMOS hitos
// agrupados por rama, y el PDF lo produce el navegador al imprimir. Se pide por el cliente de
// API y se abre en una pestaña nueva: así un 403 o un 404 llegan como error de negocio en esta
// pantalla, y no como un JSON en la pestaña nueva.
//
// FUERA DEL ÁMBITO (C8) el servidor responde 403 y aquí no se pinta ningún hito.

import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, History, Printer, RefreshCw, ShieldOff } from 'lucide-react';
import { Alert, Button, Card, DataTable, EmptyState, Mono, PageHeader } from '../components/ui';
import type { ColumnaDeTabla, FilaDeTabla } from '../components/ui';
import { consultarTrazabilidad, exportarTrazabilidad } from '../api/tableros';
import type { Hito, Trazabilidad as DatosDeLaTrazabilidad } from '../api/tableros';
import { aFallo } from '../api/cliente';
import type { Fallo } from '../api/cliente';
import { VentanaBloqueada, abrirDocumentoEnPestana } from '../lib/abrirDocumento';
import { comoFechaDeInstante, comoFechaYHora24 } from '../lib/fechas';
import { useZonaDelPais } from '../lib/CatalogosContexto';
import { TEXTO_PRIVACIDAD_DE_LA_TRAZABILIDAD, etiquetaDeLaRama } from '../lib/etiquetas';

/**
 * `dd/mm/aaaa hh:mm` en la ZONA DEL PAÍS DEL CASO (RN-82); un dato guardado como día va sin hora.
 *
 * Las horas iban fijas en Lima porque ninguna fuente fijaba otra zona para la trazabilidad (vacío 8
 * de la spec). Desde la decisión del operador del 6-oct-2026 (Bug #1087) toda hora de un caso se
 * lee en la zona de su país, la misma de la que sale su día: un caso de Chile dice la hora de
 * Chile. El documento exportado imprime las horas en esa misma zona y la declara, así que pantalla
 * e impreso siguen diciendo la misma hora. El día sin hora el servidor lo fecha a las 00:00 de esa
 * zona, y leído en ella es su día.
 */
function fechaDelHito(hito: Hito, zonaDelCaso: string | null): string {
  if (hito.solo_fecha) return comoFechaDeInstante(hito.ocurrido_en, zonaDelCaso);
  return comoFechaYHora24(hito.ocurrido_en, zonaDelCaso);
}

/** Sin pestaña nueva no hay donde mostrar el documento (texto no fijado por la spec: duda). */
const TEXTO_VENTANA_BLOQUEADA =
  'El navegador no dejó abrir la pestaña del documento. Permite las ventanas de este sitio y vuelve a exportar.';

/** Versión, IP y motivo, una línea por dato, igual que el documento exportado. */
function detalleDelHito(hito: Hito): string[] {
  const lineas: string[] = [];
  if (hito.version !== null) lineas.push(`Versión ${hito.version}`);
  if (hito.direccion_ip) lineas.push(`IP ${hito.direccion_ip}`);
  if (hito.motivo) lineas.push(`Motivo: ${hito.motivo}`);
  return lineas;
}

/** El hito dentro de la celda de su rama: qué pasó, quién y el detalle de la constancia. */
function CeldaDelHito({ hito }: { hito: Hito }) {
  const detalle = detalleDelHito(hito);
  return (
    <div className="flex flex-col gap-0.5 min-w-[9rem]">
      <span data-testid="trazabilidad-hito-evento" className="text-body font-semibold text-ink">{hito.texto}</span>
      <span data-testid="trazabilidad-hito-responsable" className="text-meta text-ink-soft">{hito.actor}</span>
      {detalle.length > 0 && (
        <span data-testid="trazabilidad-hito-detalle" className="flex flex-col text-meta text-ink-soft">
          {detalle.map((l) => <span key={l} className="font-mono">{l}</span>)}
        </span>
      )}
    </div>
  );
}

/** La tabla de carriles: «Fecha y hora» y una columna por rama que el caso tiene. */
function LineaDeTiempo({ trazabilidad }: { trazabilidad: DatosDeLaTrazabilidad }) {
  const zonaDelCaso = useZonaDelPais()(trazabilidad.pais);
  const columnas: ColumnaDeTabla[] = [
    { key: 'fecha', label: 'Fecha y hora', testid: 'trazabilidad-hito-fecha', className: 'whitespace-nowrap' },
    ...trazabilidad.ramas.map((rama) => ({
      key: `rama:${rama}`,
      label: <span data-testid={`trazabilidad-rama-${rama.split('_').join('-')}`}>{etiquetaDeLaRama(rama)}</span>,
    })),
  ];
  const filas: FilaDeTabla[] = trazabilidad.hitos.map((hito, i) => ({ ...hito, id: `${hito.ocurrido_en}-${i}` }));
  const celda = (fila: FilaDeTabla, { key }: ColumnaDeTabla) => {
    const hito = fila as unknown as Hito;
    if (key === 'fecha') return <Mono className="text-meta text-ink">{fechaDelHito(hito, zonaDelCaso)}</Mono>;
    return key === `rama:${hito.rama}` ? <CeldaDelHito hito={hito} /> : null;
  };
  return (
    // Sin controles dentro, la tabla ancha no se alcanza con el teclado: la caja que desplaza
    // entra en el orden de tabulación como región nombrada (WCAG 2.1.1).
    <DataTable testidDeFila="trazabilidad-hito" columns={columnas} rows={filas}
      renderCell={celda} sinMarco regionDesplazable="Línea de tiempo del caso" />
  );
}

export default function Trazabilidad({ casoId, textoDeVolver, onVolver }: {
  casoId: string;
  /** «Volver al expediente»: dice a dónde vuelve, no solo «Volver». */
  textoDeVolver?: string;
  onVolver?: () => void;
}) {
  const [trazabilidad, setTrazabilidad] = useState<DatosDeLaTrazabilidad | null>(null);
  const [cargando, setCargando] = useState(true);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [exportando, setExportando] = useState(false);
  const [falloAlExportar, setFalloAlExportar] = useState<Fallo | null>(null);
  const [exportada, setExportada] = useState(false);
  const enCurso = useRef<AbortController | null>(null);

  const cargar = useCallback(async () => {
    enCurso.current?.abort();
    const control = new AbortController();
    enCurso.current = control;
    setCargando(true);
    setFallo(null);
    try {
      const respuesta = await consultarTrazabilidad(casoId, { senal: control.signal });
      if (!control.signal.aborted) setTrazabilidad(respuesta);
    } catch (error) {
      if (control.signal.aborted || (error instanceof Error && error.name === 'AbortError')) return;
      setTrazabilidad(null);
      setFallo(aFallo(error));
    } finally {
      if (!control.signal.aborted) setCargando(false);
    }
  }, [casoId]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => () => enCurso.current?.abort(), []);

  /**
   * HU-37-C9. La pestaña se abre EN EL CLIC y recibe el documento cuando llega; si falla, se
   * cierra y el error queda aquí, con la tabla a la vista. El mecanismo es el común de
   * `lib/abrirDocumento.ts` (el mismo de «Ver e imprimir» del Formato Amarillo); «Exportando…»
   * se marca solo si la pestaña llegó a abrirse, como antes.
   */
  const exportar = async () => {
    setFalloAlExportar(null);
    setExportada(false);
    try {
      await abrirDocumentoEnPestana(() => {
        setExportando(true);
        return exportarTrazabilidad(casoId);
      });
      setExportada(true);
    } catch (error) {
      setFalloAlExportar(error instanceof VentanaBloqueada ? { message: TEXTO_VENTANA_BLOQUEADA } : aFallo(error));
    } finally {
      setExportando(false);
    }
  };

  const numero = trazabilidad?.numero ?? 'Caso sin número';
  const sinPermiso = fallo?.estado === 403;
  const puedeExportar = Boolean(trazabilidad?.acciones_permitidas.includes('exportar'));

  const volver = onVolver && textoDeVolver
    ? (
      <Button variant="secondary" size="sm" icon={ArrowLeft} onClick={onVolver} data-testid="trazabilidad-volver">
        {textoDeVolver}
      </Button>
    )
    : null;

  if (sinPermiso && fallo) {
    // C8: ningún hito en el DOM. El texto es el del contrato.
    return (
      <>
        {volver && <div className="mb-4">{volver}</div>}
        <Card>
          <EmptyState principal data-testid="trazabilidad-sin-permiso" icon={ShieldOff}
            title="No tienes acceso a la trazabilidad de este caso" description={fallo.message} />
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Casos · Trazabilidad"
        hu="HU-37 · MG-ILE-69"
        title={<span data-testid="trazabilidad-numero">{trazabilidad ? `Trazabilidad ${numero}` : 'Trazabilidad del caso'}</span>}
        subtitle="Cada hito del proceso con su responsable y su fecha. Las ramas que corrieron en paralelo van en columnas separadas."
        icon={History}
        actions={(
          <>
            {volver}
            {puedeExportar && (
              <Button variant="primary" size="sm" icon={Printer} onClick={exportar} disabled={exportando}
                data-testid="trazabilidad-exportar">
                {exportando ? <span role="status">Exportando…</span> : 'Exportar PDF'}
              </Button>
            )}
          </>
        )}
      />

      {trazabilidad?.afecta_a_personas && (
        <div className="mb-4">
          <Alert data-testid="trazabilidad-aviso-personas" tone="info">{TEXTO_PRIVACIDAD_DE_LA_TRAZABILIDAD}</Alert>
        </div>
      )}
      {falloAlExportar && (
        <div className="mb-4">
          <Alert data-testid="trazabilidad-error-envio" tone="danger" title="No se pudo exportar la trazabilidad."
            detalleTecnico={falloAlExportar.detalleTecnico} onCerrar={() => setFalloAlExportar(null)}>
            {falloAlExportar.message}
          </Alert>
        </div>
      )}
      {exportada && (
        <div className="mb-4">
          <Alert data-testid="trazabilidad-exito" tone="success" title="Trazabilidad exportada." onCerrar={() => setExportada(false)}>
            El documento «{numero} · Trazabilidad» se abrió en otra pestaña; desde ahí se imprime o se guarda como PDF.
          </Alert>
        </div>
      )}
      {fallo && (
        <div className="mb-4">
          <Alert data-testid="trazabilidad-error" tone="danger" title="No se pudo cargar la trazabilidad"
            detalleTecnico={fallo.detalleTecnico}>
            {fallo.message}
            {fallo.estado === 0 && ' Revisa tu conexión e intenta de nuevo.'}
            <span className="block mt-2">
              <Button size="sm" variant="secondary" icon={RefreshCw} onClick={cargar} data-testid="trazabilidad-reintentar">
                Reintentar
              </Button>
            </span>
          </Alert>
        </div>
      )}

      <Card pad={false}>
        {cargando && (
          <p data-testid="trazabilidad-cargando" role="status" className="p-6 text-body text-ink-soft">Cargando la trazabilidad…</p>
        )}
        {!cargando && trazabilidad && (trazabilidad.hitos.length
          ? <LineaDeTiempo trazabilidad={trazabilidad} />
          : <EmptyState data-testid="trazabilidad-vacio" icon={History} title="Este caso todavía no tiene hitos registrados." />)}
      </Card>
    </>
  );
}
